import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  /** Libellé de la zone protégée (ex. « Vue 3D »). */
  label: string;
  children: ReactNode;
  /** Fallback personnalisé ; sinon on affiche le panneau par défaut. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * ErrorBoundary — filet de sécurité React.
 *
 * Rattrape toute exception levée pendant le rendu d'un sous-arbre (y compris le
 * rejet d'un `import()` dynamique / chunk manquant après un redéploiement) et
 * dégrade proprement en message local, AU LIEU de démonter toute l'application
 * (écran blanc). Indispensable autour des routes lazy servies hors AppShell.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Trace console pour le diagnostic (visible via read_console_messages / DevTools).
    console.error(`[ErrorBoundary:${this.props.label}]`, error, info.componentStack);
  }

  private isChunkLoadError(): boolean {
    const msg = this.state.error?.message ?? '';
    return /dynamically imported module|Loading chunk|Failed to fetch/i.test(msg);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return <>{this.props.fallback}</>;

    const chunk = this.isChunkLoadError();
    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center p-8">
        <div
          className="max-w-xl rounded-panel border border-[#f3c4c4] bg-[#fff7f7] px-6 py-5"
          role="alert"
        >
          <p className="font-display text-base font-bold text-ink">
            {this.props.label} indisponible
          </p>
          <p className="mt-2 text-sm text-muted">
            {chunk
              ? "Cette vue n'a pas pu être chargée (ressource manquante, probablement après une mise à jour). Rechargez la page pour récupérer la dernière version."
              : "Une erreur est survenue lors de l'affichage de cette vue. Le reste de l'application reste utilisable."}
          </p>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white"
            >
              Recharger
            </button>
            <a
              href="/"
              className="rounded-lg border border-[#d9dde3] px-4 py-2 text-sm font-semibold text-ink"
            >
              Retour au tableau de bord
            </a>
          </div>
        </div>
      </div>
    );
  }
}
